import React, { useState, useEffect, useContext } from 'react'
import { Form, Button, Container, Alert, Row, Col } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import Message from '../components/Message'
import Loader from '../components/Loader'
import { getOwnerProfile, updateOwnerProfile } from '../actions/ownerActions.js'
import { OWNER_UPDATE_PROFILE_RESET } from '../constants/ownerConstants.js'
import { ThemeContext } from '../components/ThemeContext.js'
import moment from 'moment'

const MAX_LOGO_BYTES = 512 * 1024
const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif']

const OwnerProfileScreen = () => {
  const { theme } = useContext(ThemeContext)

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [receiptLogoDataUrl, setReceiptLogoDataUrl] = useState('')
  const [receiptLogoFileName, setReceiptLogoFileName] = useState('')
  const [logoChanged, setLogoChanged] = useState(false)
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [message, setMessage] = useState(null)
  const [alert, setAlert] = useState({
    show: false,
    message: '',
    variant: 'success'
  })

  function showAlert(alertMessage, variant = 'success', seconds = 3000) {
    setAlert({
      show: true,
      message: alertMessage,
      variant
    })

    setTimeout(() => {
      setAlert({
        show: false,
        message: '',
        variant: 'success'
      })
    }, seconds)
  }

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const ownerProfile = useSelector((state) => state.ownerProfile)
  const { loading, error, owner } = ownerProfile

  const ownerLogin = useSelector((state) => state.ownerLogin)
  const { ownerInfo } = ownerLogin

  const ownerUpdateProfile = useSelector((state) => state.ownerUpdateProfile)
  const {
    loading: loadingUpdate,
    error: errorUpdate,
    success
  } = ownerUpdateProfile

  const panelStyle = {
    backgroundColor: theme.card,
    border: `1px solid ${theme.sidebarAccent}`,
    borderRadius: '12px',
    padding: '1.25rem'
  }

  const labelStyle = {
    color: theme.subtext
  }

  const inputStyle = {
    backgroundColor: theme.surfaceMuted,
    color: theme.text,
    borderColor: theme.sidebarAccent
  }

  useEffect(() => {
    if (!ownerInfo) {
      navigate('/owner/login')
      return
    }

    if (!owner || !owner.name) {
      dispatch(getOwnerProfile())
    } else {
      setName(owner.name)
      setEmail(owner.email)
      setReceiptLogoDataUrl(owner.receiptLogoDataUrl || '')
      setReceiptLogoFileName(owner.receiptLogoFileName || '')
      setLogoChanged(false)
    }
  }, [dispatch, navigate, ownerInfo, owner])

  useEffect(() => {
    if (success) {
      showAlert('Owner profile updated')
      setPassword('')
      setConfirmPassword('')
      dispatch({ type: OWNER_UPDATE_PROFILE_RESET })
      dispatch(getOwnerProfile())
    }
  }, [dispatch, success])

  const handleReceiptLogoChange = (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    setMessage(null)
    if (!LOGO_TYPES.includes(file.type)) {
      setMessage('Logo must be a PNG, JPEG, WebP or GIF image.')
      e.target.value = ''
      return
    }
    if (file.size > MAX_LOGO_BYTES) {
      setMessage(
        `Logo must be smaller than ${MAX_LOGO_BYTES / 1024} KB (this one is ${Math.round(file.size / 1024)} KB).`
      )
      e.target.value = ''
      return
    }

    const reader = new FileReader()
    reader.onload = () => {
      setReceiptLogoDataUrl(String(reader.result || ''))
      setReceiptLogoFileName(file.name)
      setLogoChanged(true)
    }
    reader.readAsDataURL(file)
  }

  // A newly picked file is only discarded locally; a saved logo is deleted
  // right away so the receipts stop using it without a separate Update click.
  const handleRemoveLogo = () => {
    const input = document.getElementById('receiptLogo')
    if (input) input.value = ''

    if (logoChanged) {
      setReceiptLogoDataUrl(owner?.receiptLogoDataUrl || '')
      setReceiptLogoFileName(owner?.receiptLogoFileName || '')
      setLogoChanged(false)
      return
    }

    if (!window.confirm('Remove the receipt header logo?')) return
    setReceiptLogoDataUrl('')
    setReceiptLogoFileName('')
    dispatch(updateOwnerProfile({ removeReceiptLogo: true }))
  }

  const submitHandler = (e) => {
    e.preventDefault()
    setMessage(null)

    if (password !== confirmPassword) {
      setMessage('Passwords do not match')
      return
    }

    dispatch(
      updateOwnerProfile({
        name,
        email,
        password,
        ...(logoChanged && { receiptLogoDataUrl, receiptLogoFileName })
      })
    )
  }

  return (
    <Container className='mt-3' style={{ color: theme.text }}>
      <Row className='justify-content-center'>
        <Col xs={12} lg={10} xl={8}>
          <div style={panelStyle}>
            <h1 style={{ color: theme.text }}>Owner Profile</h1>
            {alert.show && (
              <Alert variant={alert.variant}>{alert.message}</Alert>
            )}
            {message && <Message variant='danger'>{message}</Message>}
            {loadingUpdate && <Loader />}
            {errorUpdate && <Message variant='danger'>{errorUpdate}</Message>}

            {loading ?
              <Loader />
            : error ?
              <Message variant='danger'>{error}</Message>
            : <Form onSubmit={submitHandler}>
                <Row>
                  <Col md={6}>
                    <Form.Group className='mb-2' controlId='name'>
                      <Form.Label style={labelStyle}>Name</Form.Label>
                      <Form.Control
                        readOnly
                        type='text'
                        value={name}
                        style={inputStyle}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className='mb-2' controlId='email'>
                      <Form.Label style={labelStyle}>Email Address</Form.Label>
                      <Form.Control
                        readOnly
                        type='email'
                        value={email}
                        style={inputStyle}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className='mb-2' controlId='cid'>
                      <Form.Label style={labelStyle}>CID</Form.Label>
                      <Form.Control
                        readOnly
                        type='text'
                        value={owner.cid || 'Not available'}
                        style={inputStyle}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className='mb-2' controlId='businessName'>
                      <Form.Label style={labelStyle}>Business Name</Form.Label>
                      <Form.Control
                        readOnly
                        type='text'
                        value={owner.bname || 'Not available'}
                        style={inputStyle}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className='mb-2' controlId='phone'>
                      <Form.Label style={labelStyle}>Phone</Form.Label>
                      <Form.Control
                        readOnly
                        type='text'
                        value={owner.phone || 'Not available'}
                        style={inputStyle}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className='mb-2' controlId='businessAddress'>
                      <Form.Label style={labelStyle}>
                        Business Address
                      </Form.Label>
                      <Form.Control
                        readOnly
                        type='text'
                        value={
                          [
                            owner.address,
                            owner.city,
                            owner.state,
                            owner.country
                          ]
                            .filter(Boolean)
                            .join(', ') || 'Not available'
                        }
                        style={inputStyle}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <Form.Group className='mb-2' controlId='emailVerification'>
                      <Form.Label style={labelStyle}>
                        Email Verification
                      </Form.Label>
                      <Form.Control
                        readOnly
                        type='text'
                        value={
                          owner.emailVerified ? 'Verified' : 'Not verified'
                        }
                        style={inputStyle}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className='mb-2' controlId='accountStatus'>
                      <Form.Label style={labelStyle}>Account Status</Form.Label>
                      <Form.Control
                        readOnly
                        type='text'
                        value={owner.isDisabled ? 'Disabled' : 'Active'}
                        style={inputStyle}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className='mb-2' controlId='lastLogin'>
                      <Form.Label style={labelStyle}>Last Login</Form.Label>
                      <Form.Control
                        readOnly
                        type='text'
                        value={
                          owner.lastLogin && moment(owner.lastLogin).isValid() ?
                            moment(owner.lastLogin).format('MM/DD/YYYY HH:mm')
                          : 'Not available'
                        }
                        style={inputStyle}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col xs={12}>
                    <Form.Group className='mb-2' controlId='receiptLogo'>
                      <Form.Label style={labelStyle}>
                        Receipt Header Logo
                      </Form.Label>
                      <Form.Control
                        type='file'
                        accept='image/png,image/jpeg,image/webp,image/gif'
                        onChange={handleReceiptLogoChange}
                        style={inputStyle}
                      />
                      <Form.Text style={{ color: theme.subtext }}>
                        PNG, JPEG, WebP or GIF, up to 512 KB. Stored privately
                        with your account and printed above the receipt text.
                        Click Update to save.
                      </Form.Text>
                    </Form.Group>

                    {receiptLogoDataUrl && (
                      <div className='mt-2 mb-3'>
                        <div
                          style={{
                            color: theme.subtext,
                            marginBottom: '0.5rem'
                          }}
                        >
                          Preview
                        </div>
                        <img
                          src={receiptLogoDataUrl}
                          alt='Receipt header preview'
                          style={{
                            maxWidth: '240px',
                            maxHeight: '120px',
                            objectFit: 'contain',
                            background: '#fff',
                            borderRadius: '8px',
                            padding: '8px'
                          }}
                        />
                        {receiptLogoFileName && (
                          <div style={{ color: theme.subtext }}>
                            {receiptLogoFileName}
                          </div>
                        )}
                        <Button
                          variant='outline-danger'
                          size='sm'
                          className='mt-2'
                          onClick={handleRemoveLogo}
                        >
                          {logoChanged ? 'Discard new logo' : 'Remove logo'}
                        </Button>
                      </div>
                    )}
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className='mb-2' controlId='password'>
                      <Form.Label style={labelStyle}>Password</Form.Label>
                      <Form.Control
                        type='password'
                        placeholder='Enter new password'
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        style={inputStyle}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className='mb-2' controlId='confirmPassword'>
                      <Form.Label style={labelStyle}>
                        Confirm Password
                      </Form.Label>
                      <Form.Control
                        type='password'
                        placeholder='Confirm new password'
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        style={inputStyle}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Button type='submit' className='btn btn-outline-primary mt-2'>
                  Update
                </Button>
              </Form>
            }
          </div>
        </Col>
      </Row>
    </Container>
  )
}

export default OwnerProfileScreen
